import { styled } from "@mui/material/styles";
import { ReasoningIcon, Spinner, usePresence } from "@UI";
import React, { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Markdown } from "../Markdown";
import { reveal, StepChevron, StepRow } from "./ToolPart";
const SUMMARY_MAX_CHARS = 80;
const Body = styled(Markdown)({
    margin: "0 0 var(--j-sp-1) 6px",
    padding: "var(--j-sp-0-5) 0 var(--j-sp-0-5) var(--j-sp-3)",
    borderLeft: "1px solid var(--j-line)",
    fontSize: "var(--j-fs-sm)",
    lineHeight: "var(--j-lh-sm)",
    color: "var(--j-ink-2)",
    "& p": {
        margin: "0 0 var(--j-sp-1-5)"
    },
    "& h1, & h2, & h3, & h4, & h5, & h6": {
        fontSize: "var(--j-fs-sm)",
        lineHeight: "var(--j-lh-sm)",
        margin: "var(--j-sp-1-5) 0 var(--j-sp-0-5)"
    }
});
function stripMarkdown(line) {
    return line.replace(/^#+\s*/, "").replace(/[*`~]/g, "").trim();
}
export function splitReasoning(text, language) {
    if (!language.startsWith("en")) return {
        heading: "",
        body: text.trim()
    };
    const lines = text.split("\n");
    const firstIndex = lines.findIndex((line)=>line.trim());
    if (firstIndex === -1) return {
        heading: "",
        body: ""
    };
    const firstLine = stripMarkdown(lines[firstIndex]);
    const heading = firstLine.length > SUMMARY_MAX_CHARS ? `${firstLine.slice(0, SUMMARY_MAX_CHARS).trimEnd()}…` : firstLine;
    return {
        heading,
        body: lines.slice(firstIndex + 1).join("\n").trim()
    };
}
const Reveal = styled("div")(reveal);
export function ReasoningPart({ part, ctx }) {
    const { t, i18n } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const presence = usePresence(isOpen);
    const bodyId = useId();
    const isStreaming = part.state === "streaming" && ctx.isStreaming;
    const { heading, body } = splitReasoning(part.text, i18n.language);
    const label = heading || t(isStreaming ? "chat.activity.thinking" : "chat.activity.reasoning");
    const content = /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement("span", {
        className: "step-icon",
        "data-live": isStreaming
    }, isStreaming ? /*#__PURE__*/ React.createElement(Spinner, {
        size: "sm"
    }) : /*#__PURE__*/ React.createElement(ReasoningIcon, {
        size: 14
    })), /*#__PURE__*/ React.createElement("span", {
        className: "step-label"
    }, label), body && /*#__PURE__*/ React.createElement(StepChevron, {
        size: 12
    }));
    if (!body) return /*#__PURE__*/ React.createElement(StepRow, {
        as: "div"
    }, content);
    return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(StepRow, {
        type: "button",
        "aria-expanded": isOpen,
        "aria-controls": isOpen ? bodyId : undefined,
        onClick: ()=>setIsOpen((open)=>!open)
    }, content), isOpen && presence.isMounted && /*#__PURE__*/ React.createElement(Reveal, {
        id: bodyId,
        "data-visible": presence.isVisible && isOpen
    }, /*#__PURE__*/ React.createElement(Body, null, body)));
}
